import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Paragraph, Spacer, TextButton, IconChevronUp, IconChevronDown, Anchor } from '@zillow/constellation';
import API from '@zillow/mortgage-api-client';
import { yieldCallback } from '@zillow/yield-callback';
import React, { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';
import CheckboxField from '../Fields/CheckboxField.js';
import SelectField from '../Fields/SelectField.js';
import TextField from '../Fields/TextField.js';
import DownPaymentField from './DownPaymentField.js';
import PropertyTaxField from './PropertyTaxField.js';
import { sendFieldInteractionEvent, legalPopoverInteractionEventCmsInfo } from './VALoanCalculator.Clickstream.js';
import { FieldConfig, fhaCalculatorDisclaimerText, vaCalculatorDisclaimerText, useCustomQuotesURL } from './VALoanCalculator.utils.js';

function VALoanCalculatorFields(_ref) {
  var isFHAEnabled = _ref.isFHAEnabled;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      'flex-shrink': '0',
      '@media $lg': {
        'margin-right': '$spacing.md'
      },
      '@media $md': {
        'flex-basis': '30%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Home price",
    name: FieldConfig.homePrice.name,
    leftAdornment: "$",
    config: FieldConfig.homePrice.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Home price', 'form_field');
    },
    required: true
  }), /*#__PURE__*/React.createElement(DownPaymentField, {
    isFHAEnabled: isFHAEnabled
  }), !isFHAEnabled && /*#__PURE__*/React.createElement(TextField, {
    label: "ZIP code",
    name: FieldConfig.zipCode.name,
    autoComplete: "postal-code",
    onChange: function onChange() {
      sendFieldInteractionEvent('ZIP code', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(SelectField, {
    options: isFHAEnabled ? FieldConfig.fhaLoanProgram.options : FieldConfig.loanProgram.options,
    label: "Loan program",
    name: FieldConfig.loanProgram.name,
    config: FieldConfig.loanProgram.config,
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Loan program', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    required: true,
    rightAdornment: "%",
    name: FieldConfig.interestRate.name,
    config: FieldConfig.interestRate.config,
    textButtonName: "See current rates",
    textButtonHref: CurrentRatesButtonUrl(),
    autoComplete: "off",
    tooltip: {
      title: 'Interest rate',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics.") : /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Interest rate'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Interest rate', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    icon: advanced ? /*#__PURE__*/React.createElement(IconChevronUp, null) : /*#__PURE__*/React.createElement(IconChevronDown, null),
    iconSize: "xs",
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (!isFHAEnabled) {
        if (advanced) {
          sendFieldInteractionEvent('Simple', 'toggle');
        } else {
          sendFieldInteractionEvent('Advanced', 'toggle');
        }
      }
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, isFHAEnabled && /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include MIP",
    name: FieldConfig.includeMIP.name,
    tooltip: {
      title: 'Include MIP',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "FHA loans require two forms of mortgage insurance premiums (MIP) to protect lenders against losses that can occur when a borrower defaults on a mortgage loan. Annual MIP and Upfront MIP. This calculator will include Annual MIP.", ' ', /*#__PURE__*/React.createElement(Anchor, {
        href: "https://www.zillow.com/learn/fha-mortgage-insurance/",
        target: "_blank"
      }, "Learn more about MIP"), ".")
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include taxes/insurance",
    name: FieldConfig.includeTaxesInsurance.name,
    tooltip: {
      title: 'Taxes and insurance',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, mortgage insurance premium, and homeowners association dues.") : /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Include taxes/insurance'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Include taxes/insurance', 'toggle');
    }
  }), /*#__PURE__*/React.createElement(PropertyTaxField, {
    isFHAEnabled: isFHAEnabled
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Home insurance",
    required: true,
    leftAdornment: "$",
    rightAdornment: "/year",
    name: FieldConfig.homeInsurance.name,
    config: FieldConfig.homeInsurance.config,
    format: commaSeparateNumber,
    autoComplete: "off",
    tooltip: {
      title: 'Home insurance',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from various events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance.") : /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. The VA calculator includes estimated home insurance premiums."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Home insurance'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('Home insurance', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "HOA dues",
    required: true,
    leftAdornment: "$",
    rightAdornment: "/mo",
    name: FieldConfig.hoa.name,
    config: FieldConfig.hoa.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Homeowners association dues',
      body: isFHAEnabled ? /*#__PURE__*/React.createElement(Paragraph, null, "Some condominiums or townhome communities are owned by a homeowners association (HOA), which requires owners to pay a monthly fee to cover shared amenities or services, such as garbage collection, landscaping, snow removal and pool maintenance.") : /*#__PURE__*/React.createElement(Paragraph, null, "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance."),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'HOA dues'
      }
    },
    onChange: function onChange() {
      return !isFHAEnabled && sendFieldInteractionEvent('HOA dues', 'form_field');
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: isFHAEnabled ? fhaCalculatorDisclaimerText : vaCalculatorDisclaimerText,
    interactionEventCmsInfo: !isFHAEnabled ? legalPopoverInteractionEventCmsInfo : ''
  }));
}
function CurrentRatesButtonUrl() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  var mortgateRatesUrl = useCustomQuotesURL('mortgage-rates', {
    value: lastValidFields.current[FieldConfig.homePrice.name],
    down: lastValidFields.current[FieldConfig.downPayment.name],
    auto: true,
    zip: API.zipcode && API.zipcode.length === 5 ? API.zipcode : null,
    source: 'Z_Mortgage_Calc_rates'
  });
  return mortgateRatesUrl;
}

export { VALoanCalculatorFields as default };
